import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ChevronRight, AlertTriangle } from 'lucide-react'
import { service } from '../lib/api/service'
import { entryToATA } from '../lib/api/transform'
import type { Project, ATAView } from '../lib/api/types'
import { formatSEK } from '../utils/calculations'
import StatusBadge from './StatusBadge'

export default function SpecifikationerScreen() {
  const navigate = useNavigate()

  const [project, setProject] = useState<Project | null>(null)
  const [atas, setAtas]       = useState<ATAView[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')

  useEffect(() => {
    Promise.all([service.getProject(), service.getAllEntries(), service.getSurcharges()])
      .then(([proj, entries, sc]) => {
        setProject(proj)
        setAtas(entries.map(e => entryToATA(e, sc)))
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const medSpec    = atas.filter(a => a.harSpec)
  const utanSpec   = atas.filter(a => !a.harSpec && a.status !== 'upplagd')

  const totalRader   = medSpec.reduce((s, a) => s + a.antalRader, 0)
  const prissattVarde = medSpec.reduce((s, a) => s + (a.belopp ?? 0), 0)
  const overlamnat   = medSpec
    .filter(a => ['overlamnad','godkand','fakturerad'].includes(a.status))
    .reduce((s, a) => s + (a.belopp ?? 0), 0)
  const godkantVarde = medSpec
    .filter(a => ['godkand','fakturerad'].includes(a.status))
    .reduce((s, a) => s + (a.belopp ?? 0), 0)

  const formatKort = (n: number | null) => {
    if (!n) return '–'
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 }) + ' mkr'
    if (Math.abs(n) >= 1_000)    return (n / 1_000).toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + ' tkr'
    return n.toLocaleString('sv-SE') + ' kr'
  }

  const filtered = medSpec.filter(a => {
    const q = search.toLowerCase()
    return !q || String(a.sortOrder).includes(q) || a.rubrik.toLowerCase().includes(q)
  })

  const projectLine = project
    ? [project.projektnr, project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : null

  return (
    <div>
      <div className="page-header">
        <div>
          {projectLine && <div className="page-eyebrow">{projectLine}</div>}
          <h1 className="page-title">Specifikationer</h1>
          <div className="page-sub">
            Prissatta ÄTA-specifikationer med rader, à-priser och påslag.
            Saknade specifikationer visas separat nedan.
          </div>
        </div>
      </div>

      {/* Totals strip */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPITile
          label="Antal specar"
          value={medSpec.length.toString()}
          sub={`${totalRader} rader totalt`}
        />
        <KPITile
          label="Prissatt värde"
          value={formatKort(prissattVarde)}
          sub="Summa av alla specar"
        />
        <KPITile
          label="Överlämnat"
          value={formatKort(overlamnat)}
          sub="Skickat till beställare"
        />
        <KPITile
          label="Godkänt värde"
          value={formatKort(godkantVarde)}
          sub="Av beställare"
          accent
        />
      </div>

      {/* Search */}
      <div className="view-toolbar">
        <span className="view-toolbar-title">Specifikationer</span>
        <span style={{ borderLeft: '.5px solid var(--border)', paddingLeft: 10, marginLeft: 2, fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink-faint)' }}>
          {medSpec.length}
        </span>
        <div className="view-toolbar-spacer" />
        <div className="search-input">
          <Search size={13} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Sök ÄTA…"
          />
        </div>
      </div>

      {/* Table */}
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 80 }}>Nr</th>
              <th>Rubrik</th>
              <th style={{ width: 140 }}>Status</th>
              <th className="num" style={{ width: 60 }}>Rader</th>
              <th className="num" style={{ width: 130 }}>Summa</th>
              <th style={{ width: 28 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-faint)', padding: 24 }}>
                  {medSpec.length === 0 ? 'Inga specifikationer skapade ännu' : 'Inga ÄTA matchar sökningen'}
                </td>
              </tr>
            )}
            {filtered.map(a => (
              <tr key={a.sortOrder} onClick={() => navigate(`/ata/${a.sortOrder}`)}>
                <td className="mono">ÄTA-{String(a.sortOrder).padStart(3, '0')}</td>
                <td style={{ fontWeight: 500 }}>{a.rubrik || <span style={{ color: 'var(--ink-faint)' }}>–</span>}</td>
                <td><StatusBadge status={a.status} small /></td>
                <td className="num mono" style={{ color: 'var(--ink-dim)' }}>{a.antalRader}</td>
                <td className="num">{a.belopp != null ? formatSEK(a.belopp) : <span style={{ color: 'var(--ink-faint)' }}>–</span>}</td>
                <td style={{ textAlign: 'center' }}>
                  <ChevronRight size={14} style={{ color: 'var(--ink-faint)' }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ÄTA utan specifikation */}
      {utanSpec.length > 0 && (
        <div className="card" style={{ marginTop: 24, padding: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 18px', borderBottom: '.5px solid var(--border)',
            background: 'var(--bg-2)', borderRadius: '10px 10px 0 0',
          }}>
            <AlertTriangle size={15} style={{ color: 'oklch(0.55 0.13 75)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>ÄTA utan specifikation</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-faint)' }}>
              {utanSpec.length}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {utanSpec.map((a, i) => (
              <div
                key={a.sortOrder}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 18px', cursor: 'pointer',
                  borderBottom: i < utanSpec.length - 1 ? '.5px solid var(--border)' : 'none',
                  borderRight: i % 2 === 0 ? '.5px solid var(--border)' : 'none',
                }}
                onClick={() => navigate(`/ata/${a.sortOrder}`)}
              >
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-faint)', flexShrink: 0 }}>
                  ÄTA-{String(a.sortOrder).padStart(3, '0')}
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.rubrik || <span style={{ color: 'var(--ink-faint)' }}>(ingen rubrik)</span>}
                </span>
                <StatusBadge status={a.status} small />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function KPITile({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={accent ? { color: 'oklch(0.40 0.12 145)' } : {}}>{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  )
}
